import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, Eye, Upload, Users, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function SponsorsPage() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: '', account_manager_ids: [], contract_start: '', contract_end: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    Promise.all([api.get('/api/sponsors'), api.get('/api/account-managers')])
      .then(([s, m]) => { setSponsors(s.data); setManagers(m.data); })
      .catch(() => toast.error('Failed to load sponsors'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.company_name.trim()) { toast.error(t('fill_required')); return; }
    try {
      const payload = { ...form, account_manager_ids: form.account_manager_ids.length > 0 ? form.account_manager_ids : undefined };
      await api.post('/api/sponsors', payload);
      toast.success(user?.role === 'account_manager' ? (t('sponsor_submitted_for_approval') || 'Sponsor submitted for admin approval') : t('sponsor_created'));
      setOpen(false);
      setForm({ company_name: '', account_manager_ids: [], contract_start: '', contract_end: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create sponsor'); }
  };

  const handleApproveSponsor = async (e, sponsorId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.put(`/api/sponsors/${sponsorId}/approve`);
      toast.success(t('sponsor_approved') || 'Sponsor approved');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleRejectSponsor = async (e, sponsorId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.put(`/api/sponsors/${sponsorId}/reject-approval`);
      toast.success(t('sponsor_rejected') || 'Sponsor rejected');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };

  const filtered = sponsors.filter(s => {
    const matchesSearch = s.company_name.toLowerCase().includes(search.toLowerCase()) ||
      (s.contact_name || '').toLowerCase().includes(search.toLowerCase());
    // Show active and pending_approval by default; show inactive/archived/rejected only when toggled
    if (!showInactive && s.status !== 'active' && s.status !== 'pending_approval') return false;
    return matchesSearch;
  });

  return (
    <div data-testid="sponsors-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('sponsors')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{sponsors.length} {t('sponsors_registered')}</p>
        </div>
        {hasPerm('create_sponsors') && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-sponsor-btn" className="bg-gray-900 hover:bg-gray-800 text-sm h-9 px-4">
              <Plus size={16} className="mr-1.5" /> {t('add_sponsor')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('new_sponsor')}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label className="text-base text-gray-700">{t('company_name')} *</Label><Input data-testid="sponsor-company-input" value={form.company_name} onChange={e => setForm(p => ({...p, company_name: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
              <div>
                <Label className="text-base text-gray-700">{t('account_manager')}</Label>
                <div className="mt-1 space-y-2">
                  <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                    {form.account_manager_ids.map(id => {
                      const mgr = managers.find(m => m.id === id);
                      return mgr ? (
                        <Badge key={id} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 pr-1 flex items-center gap-1">
                          {mgr.name}
                          <button type="button" onClick={() => setForm(p => ({...p, account_manager_ids: p.account_manager_ids.filter(i => i !== id)}))} className="ml-0.5 hover:text-red-500">x</button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                  <Select value="" onValueChange={v => { if (v && !form.account_manager_ids.includes(v)) setForm(p => ({...p, account_manager_ids: [...p.account_manager_ids, v]})); }}>
                    <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('select_manager')} /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {managers.filter(m => !form.account_manager_ids.includes(m.id)).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-base text-gray-700">{t('contract_start')}</Label><Input type="date" value={form.contract_start} onChange={e => setForm(p => ({...p, contract_start: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
                <div><Label className="text-base text-gray-700">{t('contract_end')}</Label><Input type="date" value={form.contract_end} onChange={e => setForm(p => ({...p, contract_end: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
              </div>
              <div><Label className="text-base text-gray-700">{t('notes')}</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="bg-gray-50 border-gray-300 text-base" /></div>
              <Button data-testid="save-sponsor-btn" onClick={handleCreate} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-10">{t('add_sponsor')}</Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="sponsor-search" placeholder={t('search_sponsors')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-gray-200 h-9 text-sm" />
        </div>
        {user?.role === 'admin' && (
          <div className="flex items-center gap-2">
          <Switch
            data-testid="show-inactive-sponsors-toggle"
            checked={showInactive}
            onCheckedChange={setShowInactive}
          />
          <span className="text-xs text-gray-500">Show inactive / archived</span>
        </div>
        )}
      </div>

      {loading ? (
        <p className="text-center py-12 text-gray-400">{t('loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-center py-12 text-gray-400">{t('no_sponsors')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => (
            <Link key={s.id} to={`/sponsors/${s.id}`} data-testid={`sponsor-card-${s.id}`}>
              <Card className="bg-white border-gray-200/80 shadow-none hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer h-full">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3.5">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.company_name} className="h-12 w-12 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center text-lg font-bold text-gray-400 shrink-0">{s.company_name?.[0]}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{s.company_name}</h3>
                        <Badge className={`text-[10px] shrink-0 pointer-events-none select-none ${
                          s.status === 'active' ? 'bg-green-100 text-green-700' :
                          s.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                          s.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-red-100 text-red-700'
                        }`}>{s.status === 'pending_approval' ? 'Pending Approval' : s.status}</Badge>
                      </div>
                    </div>
                  </div>

                  {(s.account_manager_names?.length > 0) && (
                    <div className="mt-3.5 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Users size={12} className="text-gray-400" />
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Account Managers</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {s.account_manager_names.map((name, i) => (
                          <Badge key={i} variant="outline" className="text-[11px] font-normal bg-blue-50 text-blue-600 border-blue-200">{name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {(s.linked_user_names?.length > 0) && (
                    <div className="mt-2.5 pt-2.5 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Users size={12} className="text-gray-400" />
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Sponsor Users</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {s.linked_user_names.map((name, i) => (
                          <Badge key={i} variant="outline" className="text-[11px] font-normal bg-purple-50 text-purple-600 border-purple-200">{name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {s.status === 'pending_approval' && user?.role === 'admin' && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                      <Button
                        data-testid={`approve-sponsor-${s.id}`}
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                        onClick={(e) => handleApproveSponsor(e, s.id)}
                      >
                        <Check size={13} className="mr-1" /> Approve
                      </Button>
                      <Button
                        data-testid={`reject-sponsor-${s.id}`}
                        size="sm"
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 text-xs h-8"
                        onClick={(e) => handleRejectSponsor(e, s.id)}
                      >
                        <XIcon size={13} className="mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
